import { Overlay } from 'ol';
import { array } from '../../node_modules/@lfi/jsonstat-js/dist/array.js';
import { dataSeries } from '../dataSeries.js';

/**
 * Overlay for map clicking and displaying info popup
 * See example: https://openlayers.org/en/latest/examples/popup.html
 */
export const popup = {

  /**
   * @type {?DataSeries}
   */
  rowData: null,

  /**
   * @type {JsonStatReader} NAFIDAS JSON-stat reader
   */
  _reader: null,

  /**
   * @type {Overlay}
   */
  overlay: null,

  /**
   * Dom element of the popup div
   * @type HTMLDivElement
   * @private
   */
  _container: document.getElementById('popup'),

  /**
   * Dom element of the popup content div
   * @type HTMLDivElement
   * @private
   */
  _content: document.getElementById('popup-content'),

  /**
   * Dom element of the popup close button
   * @type HTMLLinkElement
   * @private
   */
  _closer: document.getElementById('popup-closer'),

  /**
   * Creates the popup.
   * @param {JsonStatReader} jsonStatReader
   * @param {DataSeries} rowData
   * @returns {popup}
   */
  init (jsonStatReader, rowData) {
    this.rowData = rowData;
    this._reader = jsonStatReader;

    // Create an overlay to anchor the popup to the map.
    this.overlay = this._get();

    this._initEvents();

    return this;
  },

  _initEvents: function () {
    this._closer.addEventListener('click', evt => {
      evt.preventDefault();
      this.hide();
    });
    window.addEventListener('keyup', evt => {
      if (evt.key === 'Escape') {
        this.hide();
      }
    });
  },

  /**
   * Create and return the overlay.
   * @return {Overlay}
   * @private
   */
  _get () {
    return new Overlay({
      element: this._container,
      autoPan: {
        animation: {
          duration: 250
        }
      }
    });
  },

  /**
   * Update the data series.
   * @param {DataSeries} rowData
   */
  update (rowData) {
    this.rowData = rowData;
    this.hide();
  },

  /**
   * Show the popup.
   * @param {MapBrowserEvent} evt OpenLayers single click event
   * @param {Feature} feature
   * @returns {void}
   * @private
   */
  show (evt, feature) {
    const regionId = feature.getId();
    const item = this.rowData.series.find(item => item.id === String(regionId));
    if (item !== undefined) {
      this._content.innerHTML = this._renderTable(this.rowData, regionId);
      this.overlay.setPosition(evt.coordinate);
    }
  },

  /**
   * Hide the popup.
   */
  hide () {
    this.overlay.setPosition(undefined);
    this._closer.blur();
  },

  /**
   * Fires custom escape event.
   * @param {KeyboardEvent} evt
   */
  onEscape (evt) {
    if (evt.key === 'Escape') {
      this.domNode.dispatchEvent(new CustomEvent('escape'));
      this.value = '';
    }
  },

  /**
   * Create the info table for the popup.
   * @returns {String}
   * @private
   * @param {DataSeries} rowData
   * @param {Number} regionId
   */
  _renderTable (rowData, regionId) {
    let str = '<table class="popup-table">';

    let css = '';
    if (this._reader.getLabelParam('Inventur1').includes('–')) {
      css = ' header__icon--change';
    }

    const labelRegion = this._reader.getLabel(this._reader.getDimIdGeo());
    const labelConcept = this._reader.getLabel(this._reader.getDimIdMetric());
    const labelInv = this._reader.getLabelParam('Inventur1');
    const item = rowData.series.find(item => item.id === String(regionId));

    str += '<thead><tr><th colspan="2"><span class="header__icon' + css + '">' + labelInv + '</span></th></tr></thead>';
    str += '<tbody>';
    str += '<tr><th>' + labelRegion + ':</th><td>' + this._reader.getCategoryLabel(this._reader.getDimIdGeo(), regionId) + '</td></tr>';
    str += '<tr><th>' + labelConcept + ':</th><td>' + dataSeries.createStr(item) + '</td></tr>';
    str += '<tr><th>' + this._reader.getTransl('Einheit') + ':</th><td>' + this._reader.getValueUnit() + '</td></tr>';
    const dimensions = this._reader.getDimIdOthers();
    const fromTo = array.rowToLinear(rowData.rowIdx, this._reader.getDimSizes());
    dimensions.forEach((dimId, idx) => {
      const catId = this._reader.getCategoryIdByOffset(fromTo[0], idx);
      str += '<tr><th>' + this._reader.getLabel(dimId) + ':</th><td>' + this._reader.getCategoryLabel(dimId, catId) + '</td></tr>';
    });
    str += '</tbody>';
    str += '</table>';
    return str;
  }
};